import { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { api, type WorkspaceMember } from '../api/client';
import { ConfirmDialog } from '../components/ConfirmDialog';

export function Members() {
  const { currentWorkspaceId, user } = useAuth();
  const [members, setMembers] = useState<WorkspaceMember[]>([]);
  const [viewerRole, setViewerRole] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [addEmail, setAddEmail] = useState('');
  const [addRole, setAddRole] = useState('member');
  const [addError, setAddError] = useState<string | null>(null);
  const [addLoading, setAddLoading] = useState(false);
  const [removeTarget, setRemoveTarget] = useState<string | null>(null);

  const isAdmin = viewerRole === 'owner' || viewerRole === 'admin';

  useEffect(() => {
    if (!currentWorkspaceId) return;
    setLoading(true);
    api
      .getMembers(currentWorkspaceId)
      .then((data) => {
        setMembers(data.members);
        setViewerRole(data.viewer_role);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [currentWorkspaceId]);

  if (!currentWorkspaceId) {
    return <p className="text-text-secondary">No workspace found. Complete onboarding first.</p>;
  }

  if (loading) {
    return <p className="text-text-secondary">Loading team members...</p>;
  }

  if (error) {
    return (
      <div className="rounded-lg bg-[rgba(248,113,113,0.12)] p-4 text-sm text-[var(--color-error)]">{error}</div>
    );
  }

  const handleRoleChange = async (memberId: string, newRole: string) => {
    try {
      await api.updateMember(currentWorkspaceId, memberId, newRole);
      setMembers((prev) =>
        prev.map((m) => (m.id === memberId ? { ...m, role: newRole } : m)),
      );
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleRemove = async (memberId: string) => {
    try {
      await api.removeMember(currentWorkspaceId, memberId);
      setMembers((prev) => prev.filter((m) => m.id !== memberId));
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!addEmail.trim()) return;
    setAddLoading(true);
    setAddError(null);
    try {
      const newMember = await api.addMember(currentWorkspaceId, addEmail.trim(), addRole);
      setMembers((prev) => [...prev, newMember]);
      setAddEmail('');
      setAddRole('member');
      setShowAdd(false);
    } catch (err: any) {
      setAddError(err.message);
    } finally {
      setAddLoading(false);
    }
  };

  const roleColors: Record<string, string> = {
    owner: 'bg-[rgba(139,92,246,0.12)] text-accent-violet',
    admin: 'bg-[rgba(96,165,250,0.12)] text-[var(--color-info)]',
    member: 'bg-[rgba(52,211,153,0.12)] text-[var(--color-success)]',
    viewer: 'bg-surface-2 text-text-primary',
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-text-primary">Team</h1>
          <p className="mt-1 text-sm text-text-secondary">
            Manage workspace members and roles.
          </p>
        </div>
        {isAdmin && (
          <button
            onClick={() => setShowAdd(!showAdd)}
            className="rounded-lg bg-gradient-to-r from-accent-teal to-accent-violet px-4 py-2 text-sm font-medium text-text-primary hover:opacity-90"
          >
            Add Member
          </button>
        )}
      </div>

      {/* Add member form */}
      {showAdd && (
        <div className="rounded-lg border border-border-dark bg-surface-1 p-5">
          <h3 className="mb-3 font-semibold text-text-primary">Add existing user by email</h3>
          {addError && (
            <div className="mb-3 rounded bg-[rgba(248,113,113,0.12)] p-2 text-sm text-[var(--color-error)]">{addError}</div>
          )}
          <form onSubmit={handleAdd} className="flex items-end gap-3">
            <div className="flex-1">
              <label htmlFor="add-email" className="block text-sm font-medium text-text-primary">
                Email
              </label>
              <input
                id="add-email"
                type="email"
                value={addEmail}
                onChange={(e) => setAddEmail(e.target.value)}
                placeholder="user@example.com"
                className="mt-1 w-full rounded-md border border-border-dark bg-surface-2 px-3 py-2 text-sm text-text-primary placeholder:text-text-tertiary focus:border-accent-teal focus:outline-none focus:ring-1 focus:ring-accent-teal"
                required
              />
            </div>
            <div className="w-36">
              <label htmlFor="add-role" className="block text-sm font-medium text-text-primary">
                Role
              </label>
              <select
                id="add-role"
                value={addRole}
                onChange={(e) => setAddRole(e.target.value)}
                className="mt-1 w-full rounded-md border border-border-dark bg-surface-2 px-3 py-2 text-sm text-text-primary"
              >
                <option value="admin">Admin</option>
                <option value="member">Member</option>
                <option value="viewer">Viewer</option>
              </select>
            </div>
            <button
              type="submit"
              disabled={addLoading}
              className="rounded-lg bg-gradient-to-r from-accent-teal to-accent-violet px-4 py-2 text-sm font-medium text-text-primary hover:opacity-90 disabled:opacity-50"
            >
              {addLoading ? 'Adding...' : 'Add'}
            </button>
          </form>
        </div>
      )}

      {/* Members table */}
      <div className="rounded-lg border border-border-dark bg-surface-1">
        {members.length === 0 ? (
          <div className="p-8 text-center text-text-secondary">No members found.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-border-dark bg-surface-0">
                  <th className="px-4 py-3 font-medium text-text-secondary">Member</th>
                  <th className="px-4 py-3 font-medium text-text-secondary">Role</th>
                  <th className="px-4 py-3 font-medium text-text-secondary">Joined</th>
                  {isAdmin && (
                    <th className="px-4 py-3 font-medium text-text-secondary">Actions</th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-border-subtle">
                {members.map((m) => {
                  const isSelf = m.user_id === user?.id;
                  return (
                    <tr key={m.id} className="hover:bg-surface-2">
                      <td className="px-4 py-3">
                        <p className="font-medium text-text-primary">{m.display_name}</p>
                        <p className="text-xs text-text-secondary">{m.email}</p>
                      </td>
                      <td className="px-4 py-3">
                        {isAdmin && !isSelf ? (
                          <select
                            value={m.role}
                            onChange={(e) => handleRoleChange(m.id, e.target.value)}
                            className="rounded-md border border-border-dark bg-surface-2 px-2 py-1 text-xs text-text-primary"
                          >
                            <option value="owner">Owner</option>
                            <option value="admin">Admin</option>
                            <option value="member">Member</option>
                            <option value="viewer">Viewer</option>
                          </select>
                        ) : (
                          <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${roleColors[m.role] || 'bg-surface-2 text-text-primary'}`}>
                            {m.role}
                          </span>
                        )}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-text-secondary">
                        {new Date(m.joined_at).toLocaleDateString()}
                      </td>
                      {isAdmin && (
                        <td className="px-4 py-3">
                          {!isSelf ? (
                            <button
                              onClick={() => setRemoveTarget(m.id)}
                              className="rounded px-2 py-1 text-xs font-medium text-[var(--color-error)] hover:bg-[rgba(248,113,113,0.12)]"
                            >
                              Remove
                            </button>
                          ) : (
                            <span className="text-xs text-text-tertiary">You</span>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ConfirmDialog
        open={removeTarget !== null}
        title="Remove Member"
        message="This member will lose access to the workspace immediately. This action cannot be undone."
        confirmLabel="Remove Member"
        confirmVariant="danger"
        onConfirm={() => {
          if (removeTarget) handleRemove(removeTarget);
          setRemoveTarget(null);
        }}
        onCancel={() => setRemoveTarget(null)}
      />
    </div>
  );
}
